import { z } from 'zod'

/** The circles, in the order the app offers them (mirrors internal/kinds/person). */
export const CIRCLES = ['partner', 'family', 'friend', 'work', 'other'] as const
export type Circle = (typeof CIRCLES)[number]

/**
 * Someone in the owner's life. `key` is the short handle captures and share
 * links carry as the `from` label; `aka` are other names or handles that mean
 * them. Tasks and dates point at a person with a `references` edge.
 */
export const personSpec = z
  .object({
    name: z.string().min(1).max(120),
    key: z.string().regex(/^[a-z0-9_-]{1,32}$/),
    aka: z.array(z.string().min(1).max(64)).max(10).optional(),
    circle: z.enum(CIRCLES).optional(),
    /** gift ideas, sizes, what they like: private to the owner */
    notes: z.string().max(4000).optional(),
    icon: z.string().max(64).optional(),
    /** the owner themselves: dates and tasks can include them, and the app says "You" */
    me: z.boolean().optional(),
  })
  .strict()
export type PersonSpec = z.infer<typeof personSpec>

export const personStatus = z.object({}).passthrough()
export type PersonStatus = z.infer<typeof personStatus>

/**
 * A handle from a name: lowercase letters and digits, accents dropped, spaces
 * as hyphens, at most 32 ("Mary-Jane O'Neil" → "mary-jane-oneil"). Mirrors
 * person.KeyOf.
 */
export const keyOf = (name: string): string => {
  let out = ''
  let dash = false
  for (const ch of name.trim().toLowerCase().normalize('NFD')) {
    if (/\p{Mn}/u.test(ch)) continue
    if (/[a-z0-9_]/.test(ch)) {
      out += ch
      dash = false
    } else if ((ch === ' ' || ch === '-') && out && !dash) {
      out += '-'
      dash = true
    }
  }
  out = out.replace(/^-+|-+$/g, '')
  return out.length > 32 ? out.slice(0, 32).replace(/-+$/, '') : out
}
